import { createContext, ReactNode, useEffect, useReducer } from 'react';
// utils
import axios from '../utils/axios';
import { setSession } from '../utils/jwt';
// @types
import { ActionMap, AuthState, AuthUser, JWTContextType } from '../@types/auth';

import API from '../api/api';
import APIPERSONAL from '../api/personal/apipersonal';

// ----------------------------------------------------------------------

enum Types {
  Initial = 'INITIALIZE',
  Login = 'LOGIN',
  Logout = 'LOGOUT',
  Register = 'REGISTER',
}

type JWTAuthPayload = {
  [Types.Initial]: {
    isAuthenticated: boolean;
    user: AuthUser;
  };
  [Types.Login]: {
    user: AuthUser;
  };
  [Types.Logout]: undefined;
  [Types.Register]: {
    user: AuthUser;
  };
};

export type JWTActions = ActionMap<JWTAuthPayload>[keyof ActionMap<JWTAuthPayload>];

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const JWTReducer = (state: AuthState, action: JWTActions) => {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        user: action.payload.user,
      };
    case 'LOGIN':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
      };

    case 'REGISTER':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
      };

    default:
      return state;
  }
};

const AuthContext = createContext<JWTContextType | null>(null);

// ----------------------------------------------------------------------

type AuthProviderProps = {
  children: ReactNode;
};

function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(JWTReducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        //const accessToken = localStorage.getItem('accessToken');
        const lmsToken = localStorage.getItem('token_lms');
        if (lmsToken) {
          const lmsTokenOnly = lmsToken.split(',', 1)[0].split(':', 2)[1].match('"(.*?)"')![1];
          let api = new API();
          const tokenvalid = await api.checkTokenValid(lmsTokenOnly);
          if (tokenvalid) {
            let apipersonal = new APIPERSONAL();
            const responseGetProfile = await apipersonal.getPersonalInfo(lmsTokenOnly);
            const user = responseGetProfile ? responseGetProfile.data : null;

            dispatch({
              type: Types.Initial,
              payload: {
                isAuthenticated: true,
                user: user,
              },
            });
          } else {
            localStorage.removeItem('token_lms');
            dispatch({
              type: Types.Initial,
              payload: {
                isAuthenticated: false,
                user: null,
              },
            });
          }
        } else {
          dispatch({
            type: Types.Initial,
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }

        // if(lmsToken){
        //   let api = new API();
        //   const tokenvalid = await api.checkTokenValid(lmsToken);
        //   if(tokenvalid){
        //     let apipersonal = new APIPERSONAL();
        //     const responseGetProfile = await apipersonal.getPersonalInfo(lmsToken);
        //     const user = responseGetProfile? responseGetProfile.data : null ;

        //     dispatch({
        //       type: Types.Initial,
        //       payload: {
        //         isAuthenticated: true,
        //         user: user,
        //       },
        //     });
        //   }
        //   else{
        //     dispatch({
        //       type: Types.Initial,
        //       payload: {
        //         isAuthenticated: false,
        //         user: null,
        //       },
        //     });
        //   }

        // } else {
        //   dispatch({
        //     type: Types.Initial,
        //     payload: {
        //       isAuthenticated: false,
        //       user: null,
        //     },
        //   });
        // }

        // if (accessToken && isValidToken(accessToken)) {
        //   setSession(accessToken);

        //   const response = await axios.get('/api/personal/profile');
        //   const { user } = response.data;

        //   dispatch({
        //     type: Types.Initial,
        //     payload: {
        //       isAuthenticated: true,
        //       user,
        //     },
        //   });
        // } else {
        //   dispatch({
        //     type: Types.Initial,
        //     payload: {
        //       isAuthenticated: false,
        //       user: null,
        //     },
        //   });
        // }
      } catch (err) {
        console.error(err);
        dispatch({
          type: Types.Initial,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, []);

  const login = async (token: string) => {
    // const responseAuthorise = await axios.post('/api/tokens', {
    //   email,
    //   password,
    // }, {
    //   headers: {
    //     tenant
    //   }
    // });
    // const accessToken = responseAuthorise.data;
    // localStorage.setItem('tenantName', tenant);
    // localStorage.setItem('refreshToken', accessToken.refreshToken);
    // localStorage.setItem('refreshTokenExpiryTime', accessToken.refreshTokenExpiryTime);

    // setSession(accessToken.token);

    // const responseGetProfile = await axios.get('/api/personal/profile');
    let apipersonal = new APIPERSONAL();

    const responseGetProfile = await apipersonal.getPersonalInfo(token);

    let user = null;

    if (responseGetProfile != null) {
      user = responseGetProfile.data;
    }
    //const user = responseGetProfile.data;
    //console.log(user);

    dispatch({
      type: Types.Login,
      payload: {
        user,
      },
    });
  };

  const register = async (
    tenant: string,
    email: string,
    username: string,
    password: string,
    confirmPassword: string,
    firstName: string,
    lastName: string,
    phoneNumber: string
  ) => {
    const response = await axios.post(
      'api/users/self-register',
      {
        firstName,
        lastName,
        email,
        username,
        password,
        confirmPassword,
        phoneNumber,
      },
      {
        headers: {
          tenant,
        },
      }
    );
    const { accessToken, user } = response.data;

    localStorage.setItem('tenantName', tenant);
    localStorage.setItem('refreshToken', accessToken.refreshToken);
    localStorage.setItem('refreshTokenExpiryTime', accessToken.refreshTokenExpiryTime);
    localStorage.setItem('accessToken', accessToken);

    dispatch({
      type: Types.Register,
      payload: {
        user,
      },
    });
  };

  const logout = async () => {
    setSession(null);
    dispatch({ type: Types.Logout });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
        register,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
